import { FormattedMessage } from "react-intl";

import { Button } from "@ps-design-system/components/Button";
import {
  FunctionalCard,
  FunctionalCardContent,
} from "@ps-design-system/components/cards/FunctionalCard";
import { InputField } from "@ps-design-system/components/Input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@ps-design-system/components/Popover";
import {
  PriceSummary,
  PriceSummaryRow,
  PriceSummaryTotalRow,
} from "@ps-design-system/components/PriceSummary";
import { Text } from "@ps-design-system/components/Text";

import { useIntl } from "react-intl-compat";
import { getCurrencyCode } from "~/i18n/i18n.utils";

import type { ListingUseType } from "../../../../../../components/src/services/listings.service";
import { USE_TYPE_SLUG } from "../../ActivateListingsPage.consts";

type DayRateDiscountFieldProps = {
  spaceCountry: string;
  useType: ListingUseType;
};

/** The 8+ hour discount field, with a popover explaining how the discount is calculated. */
const DayRateDiscountField = ({
  spaceCountry,
  useType,
}: DayRateDiscountFieldProps) => {
  const intl = useIntl();
  const slug = USE_TYPE_SLUG[useType];

  return (
    <InputField
      fullWidth
      type="number"
      min={0}
      name="dayRateDiscountPercent"
      label={intl.formatMessage({
        id: "hosting.activate_listings.day_rate_discount_label",
        defaultMessage: "8+ hour discount",
      })}
      subcopy={intl.formatMessage({
        id: "hosting.activate_listings.day_rate_discount_note",
        defaultMessage:
          "Encourage guests to book longer by giving a discount for bookings that are 8 hours or more.",
      })}
      suffix={intl.formatMessage({
        id: "hosting.activate_listings.day_rate_discount_suffix",
        defaultMessage: "% off",
      })}
      placeholder={intl.formatMessage({
        id: "hosting.activate_listings.day_rate_discount_placeholder",
        defaultMessage: "Optional",
      })}
      testId={`activate-listing-form-${slug}-day-rate-discount-input`}
      note={
        <Popover>
          <PopoverTrigger asChild={true}>
            <Button
              variant="text-only"
              className="tw-body-1-underline tw-p-0 tw-h-auto"
              testId={`activate-listing-form-${slug}-day-rate-discount-tooltip-button`}
            >
              <FormattedMessage
                id="hosting.activate_listings.day_rate_discount_calculation"
                defaultMessage="How is this calculated?"
              />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="tw-w-[250px]" align="start">
            <FunctionalCard>
              <FunctionalCardContent className="tw-text-gray-900">
                <Text typeface="body-1" className="tw-mb-12">
                  <FormattedMessage
                    id="hosting.activate_listings.day_rate_discount_example"
                    defaultMessage="Example: If your hourly price is $100, and your discount is 20%"
                  />
                </Text>
                <PriceSummary>
                  <PriceSummaryRow
                    name={intl.formatMessage({
                      id: "hosting.activate_listings.day_rate_discount_example_hourly_rate",
                      defaultMessage: "$100 x 8 hours",
                    })}
                    value={intl.formatNumber(800, {
                      currency: getCurrencyCode(spaceCountry),
                      maximumFractionDigits: 0,
                      minimumFractionDigits: 0,
                      style: "currency",
                    })}
                  />
                  <PriceSummaryRow
                    name={intl.formatMessage({
                      id: "hosting.activate_listings.day_rate_discount_example_discount",
                      defaultMessage: "20% Discount",
                    })}
                    value={intl.formatNumber(-160, {
                      currency: getCurrencyCode(spaceCountry),
                      maximumFractionDigits: 0,
                      minimumFractionDigits: 0,
                      style: "currency",
                    })}
                  />
                  <PriceSummaryTotalRow
                    value={intl.formatNumber(640, {
                      currency: getCurrencyCode(spaceCountry),
                      maximumFractionDigits: 0,
                      minimumFractionDigits: 0,
                      style: "currency",
                    })}
                  />
                </PriceSummary>
              </FunctionalCardContent>
            </FunctionalCard>
          </PopoverContent>
        </Popover>
      }
    />
  );
};

export { DayRateDiscountField };
